<script setup lang="ts">
import VarsityInlineConfirm from "@/components/VarsityInlineConfirm.vue"
import VarsityInlineConfirmButton from "@/components/VarsityInlineConfirmButton.vue"
import { createInlineConfirmCollection } from "@/composables/createInlineConfirm"
import type { ShopOrderSubmitPayload } from "@/composables/useShop"
import type { ShopSelectProductsTask } from "@/model/order/shop-select-products-task"
import type { PaymentMethod } from "@/model/payment/payment-method"
import type { Issue } from "@/model/tasks/issue"
import ShopOrderForm from "@/pages/Tasks/Tasks/internal/ShopOrderForm.vue"
import { defineTranslations } from "@/utils/defineTranslations"
import { type Entity, useHttpClient } from "@/vf"
import { ref, useTemplateRef } from "vue"

defineOptions({ inheritAttrs: false })

const props = defineProps<{
    task: ShopSelectProductsTask
    issue?: Issue
    viewMode?: string
}>()

const emit = defineEmits<{
    (e: "updated", entity: Entity): void
}>()

const http = useHttpClient()

const shopConfigurationId = props.task.shopOrder?.shopConfiguration?.id
// Some shop areas get their own wording, every other shop area shares the generic description
const textVariant = props.task.shopOrder!.shopConfiguration!.textVariant!
const stockError = ref<string | null>(null)
const shopOrderFormRef = useTemplateRef<InstanceType<typeof ShopOrderForm>>("shopOrderForm")

async function onSubmit(payload: ShopOrderSubmitPayload, done: () => void, paymentMethod?: PaymentMethod | null) {
    stockError.value = null
    // Customers are forwarded to the checkout right away, unless the order needs an approval first
    const query = new URLSearchParams({ pay: "1" })
    if (paymentMethod) {
        query.set("paymentMethod", paymentMethod)
    }
    const url = `/order/shop/${shopConfigurationId}/${props.task.shopOrder?.id}${
        props.viewMode === "admin" ? "" : `?${query}`
    }`
    let redirecting = false
    try {
        const response = await http.put<Entity & { checkoutUrl?: string | null }>(url, payload)

        if (response.data.checkoutUrl) {
            redirecting = true
            location.href = response.data.checkoutUrl
            return
        }

        emit("updated", response.data as Entity)
    } catch (e: any) {
        if (e?.response?.status === 409 && e.response.data?.error === "insufficient_stock") {
            stockError.value = e.response.data.item
            await shopOrderFormRef.value?.refreshProducts()
        }
    } finally {
        // While redirecting, keep the button in its loading state until the browser leaves the page
        if (!redirecting) {
            done()
        }
    }
}

const cancelOrder = createInlineConfirmCollection<string>(() => ({
    action: async () => {
        await http.delete(`/order/shop/${shopConfigurationId}/${props.task.shopOrder?.id}`)
    },
    afterAction: () => emit("updated", props.task as unknown as Entity),
}))

const trans = defineTranslations({
    "description.generic.title": {
        de: "Produkte auswählen",
        en: "Select products",
    },
    "description.generic.text": {
        de: "Wähle Produkte aus",
        en: "Please select products",
    },
    "description.workshops_camps.title": {
        de: "Workshops & Camps auswählen",
        en: "Select workshops & camps",
    },
    "description.workshops_camps.text": {
        de: "Wähle die gewünschten Workshops und Camps aus",
        en: "Please select the workshops and camps you want to book",
    },
    "description.tickets.title": {
        de: "Tickets auswählen",
        en: "Select tickets",
    },
    "description.tickets.text": {
        de: "Wähle die gewünschten Tickets aus",
        en: "Please select the tickets you want to buy",
    },
    "description.private_camps.title": {
        de: "Private Camps auswählen",
        en: "Select private camps",
    },
    "description.private_camps.text": {
        de: "Wähle die gewünschten Private Camps aus",
        en: "Please select the private camps you want to book",
    },
    "cancel_order": {
        de: "Bestellvorgang abbrechen",
        en: "Cancel order",
    },
    "cancel_order.confirmation": {
        de: "Soll der Bestellvorgang wirklich abgebrochen werden?",
        en: "Do you really want to cancel this order?",
    },
    "cancel_order.success": {
        de: "Bestellvorgang abgebrochen",
        en: "Order canceled",
    },
})
</script>

<template>
    <div class="p-5">
        <b>{{ trans(`description.${textVariant}.title`) }}</b>
        <div>{{ trans(`description.${textVariant}.text`) }}</div>

        <div v-if="stockError" class="alert alert-danger mt-4">
            {{ $t("@tasks:tasks.shop.select_products.insufficient_stock", { item: stockError }) }}
        </div>

        <ShopOrderForm
            ref="shopOrderForm"
            class="mt-5"
            :shop-order-id="task.shopOrder!.id!"
            :shop-configuration-id="task.shopOrder!.shopConfiguration!.id!"
            :submit-url="`/order/shop/${shopConfigurationId}/${task.id}`"
            :issue-owner-id="issue?.owner?.id"
            @submit="onSubmit"
            :editable="task.state === 'open'"
            :view-mode="viewMode"
        />
    </div>

    <Teleport to="#taskButtons" v-if="task.state === 'open'">
        <VarsityInlineConfirmButton class="btn btn-danger" :controller="cancelOrder(task.id!)">
            <i class="fa fa-fw fa-trash"></i>
            {{ trans("cancel_order") }}
        </VarsityInlineConfirmButton>
    </Teleport>
    <Teleport to="#taskConfirmation" v-if="task.state === 'open'">
        <VarsityInlineConfirm :controller="cancelOrder(task.id!)">
            <template #confirmation>{{ trans("cancel_order.confirmation") }}</template>
            <template #success>{{ trans("cancel_order.success") }}</template>
        </VarsityInlineConfirm>
    </Teleport>
</template>
